import { defineField, defineType } from 'sanity'

import getPageInfo, { seoGroup } from '../../objects/pageInfo'

type PhaseValue = { title?: string; startDate?: string }

// Each phase ends where the next one starts, so the schedule has to read top to
// bottom in time. Missing dates are reported by the phase's own required rule.
const validatePhaseSchedule = (phases: unknown) => {
  if (!Array.isArray(phases)) return true

  const label = (index: number) => {
    const title = (phases[index] as PhaseValue)?.title
    return title ? `phase ${index + 1} ("${title}")` : `phase ${index + 1}`
  }

  for (let index = 1; index < phases.length; index++) {
    const previous = Date.parse((phases[index - 1] as PhaseValue)?.startDate ?? '')
    const current = Date.parse((phases[index] as PhaseValue)?.startDate ?? '')
    if (Number.isNaN(previous) || Number.isNaN(current)) continue

    if (current === previous) {
      return `${label(index - 1)} and ${label(index)} start at the same time. Give each phase its own start time.`
    }
    if (current < previous) {
      return `${label(index)} starts before ${label(index - 1)}. Order the phases by start time.`
    }
  }

  return true
}

export default defineType({
  title: 'Black Friday',
  name: 'blackFriday',
  type: 'document',
  groups: [
    {
      name: 'content',
      title: 'Content',
      default: true,
    },
    {
      name: 'old_content',
      title: 'Old content',
    },
    seoGroup,
  ],
  fields: [
    defineField({
      name: 'enabled',
      title: 'Enable Black Friday page',
      type: 'boolean',
      initialValue: false,
      description:
        'Keep off while preparing the campaign. When off, /blackfriday is unavailable and excluded from the sitemap. Phase dates do not enable the page. Publish and sync site content after changing this setting.',
      group: 'content',
    }),
    defineField({
      name: 'phases',
      title: 'Phases',
      type: 'array',
      of: [{ type: 'specialEventPhase' }],
      description:
        'The page switches to each phase at its start time. Studio shows times in your browser time zone; they are stored as exact moments, so double-check them against the campaign time zone.',
      validation: (rule) => [rule.min(1), rule.custom(validatePhaseSchedule)],
      group: 'content',
    }),
    defineField({
      title: 'Before Countdown Content',
      name: 'beforeCountdown',
      type: 'beforeCountdown',
      group: 'old_content',
    }),
    defineField({
      title: 'After Countdown Content',
      name: 'afterCountdown',
      type: 'afterCountdown',
      group: 'old_content',
    }),
    getPageInfo({ lockCanonical: true }),
  ],
  preview: {
    select: {
      pageInfo: 'pageInfo',
    },
    prepare(selection) {
      const { title = 'New Page', description: subtitle = '' } =
        selection.pageInfo || {}
      return {
        title,
        subtitle,
      }
    },
  },
})
